import { useEffect, useState } from 'react';

import FilterSegmentShell from './FilterSegmentShell';

// The queue filter bar's Date range control (GRAVY-10315).
//
// The approved node (38995:64197) shows Client, Skills and Location; Date range
// is product-confirmed from GRAVY-10303 and has to live somewhere, so it is a
// fourth segment in the same funnel group — same trigger, same scoped menu,
// same staging — rather than a differently-shaped control bolted to the end of
// the bar.
//
// Staged like every other segment: two date inputs that only leave on Apply, so
// picking a start does not fire a request before the end is chosen.
//
// Half-open ranges are deliberately allowed. "Everything from the 1st" and
// "everything up to the 5th" are both real questions, and the server treats a
// missing bound as unbounded on that side; requiring both would make the
// control refuse a query the API answers.

type Props = {
  startDate: string | null;
  endDate: string | null;
  onChange: (next: {
    startDate: string | null;
    endDate: string | null;
  }) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  align?: 'left' | 'right';
};

const LABEL = 'Date range';

/** "Mar 3" / "Mar 3, 2027" — the year only when it is not the current one. */
const short = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(y === new Date().getFullYear() ? {} : { year: 'numeric' }),
  }).format(date);
};

const DateRangeSegment = ({
  startDate,
  endDate,
  onChange,
  open,
  onOpenChange,
  align = 'left',
}: Props) => {
  const [start, setStart] = useState<string>(startDate ?? '');
  const [end, setEnd] = useState<string>(endDate ?? '');

  // Re-seed whenever the menu is closed, so dismissing discards the edit and
  // Reset filters (which clears both dates at once) is reflected here.
  useEffect(() => {
    if (!open) {
      setStart(startDate ?? '');
      setEnd(endDate ?? '');
    }
  }, [open, startDate, endDate]);

  const applied = !!startDate || !!endDate;

  const triggerText = !applied
    ? LABEL
    : startDate && endDate
      ? `${short(startDate)} – ${short(endDate)}`
      : startDate
        ? `From ${short(startDate)}`
        : `Until ${short(endDate!)}`;

  const apply = () => {
    onChange({ startDate: start || null, endDate: end || null });
    onOpenChange(false);
  };

  const clear = () => {
    onChange({ startDate: null, endDate: null });
    setStart('');
    setEnd('');
    onOpenChange(false);
  };

  return (
    <FilterSegmentShell
      label={LABEL}
      triggerText={triggerText}
      active={applied}
      open={open}
      onOpenChange={onOpenChange}
      align={align}
      // Not a list of options, so it is a group rather than a listbox — a
      // listbox with no options would be announced as an empty one.
      menuRole="group"
    >
      {() => (
        <>
          <div className="dashv2-fseg-dates">
            <label className="dashv2-fseg-date">
              <span>From</span>
              <input
                type="date"
                aria-label="Date range start"
                value={start}
                // A start after the end is not a range the server can answer,
                // so the inputs bound each other rather than letting it be
                // submitted and come back empty.
                max={end || undefined}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>
            <label className="dashv2-fseg-date">
              <span>To</span>
              <input
                type="date"
                aria-label="Date range end"
                value={end}
                min={start || undefined}
                onChange={(e) => setEnd(e.target.value)}
              />
            </label>
          </div>
          <div className="dashv2-fseg-foot">
            <button
              type="button"
              className="dashv2-fseg-clear"
              disabled={!applied && !start && !end}
              onClick={clear}
            >
              Clear
            </button>
            <button type="button" className="dashv2-fseg-apply" onClick={apply}>
              Apply
            </button>
          </div>
        </>
      )}
    </FilterSegmentShell>
  );
};

export default DateRangeSegment;
